import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import "./SectionStepper.css";

const SectionStepper = ({ sections = [] }) => {
  const [activeSection, setActiveSection] = useState(sections[0]?.id || "");
  const [hoveredSection, setHoveredSection] = useState(null);
  const observers = useRef([]);

  useEffect(() => {
    // Clear existing observers
    observers.current.forEach((obs) => obs.disconnect());
    observers.current = [];

    const options = {
      root: null,
      rootMargin: "-20% 0px -70% 0px", // Trigger when section is in the upper part of the viewport
      threshold: 0
    };

    const handleIntersect = (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          setActiveSection(entry.target.id);
        }
      });
    };

    const observer = new IntersectionObserver(handleIntersect, options);
    
    sections.forEach((section) => {
      const el = document.getElementById(section.id);
      if (el) {
        observer.observe(el);
      }
    });

    observers.current.push(observer);

    return () => {
      observer.disconnect();
    };
  }, [sections]);

  const scrollToSection = (id) => {
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: "smooth" });
      // For Lenis compatibility if it's active
      if (window.lenis && typeof window.lenis.scrollTo === "function") {
        window.lenis.scrollTo(el);
      }
    }
  };

  if (sections.length === 0) return null;

  return (
    <nav className="section-stepper" aria-label="Section Navigation">
      <div className="stepper-container">
        {sections.map((section, index) => (
          <div
            key={section.id}
            className={`stepper-item ${activeSection === section.id ? "active" : ""}`}
            onMouseEnter={() => setHoveredSection(section.id)}
            onMouseLeave={() => setHoveredSection(null)}
            onClick={() => scrollToSection(section.id)}
          >
            {/* Label Pill */}
            <AnimatePresence>
              {hoveredSection === section.id && (
                <motion.div
                  initial={{ opacity: 0, x: 20, scale: 0.8 }}
                  animate={{ opacity: 1, x: 0, scale: 1 }}
                  exit={{ opacity: 0, x: 10, scale: 0.9 }}
                  transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
                  className="stepper-label"
                >
                  <span className="label-number">{index + 1}</span>
                  <span className="label-text">{section.label}</span>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Dot Indicator */}
            <div className="stepper-dot">
              <div className="dot-inner" />
              {activeSection === section.id && (
                <motion.div
                  layoutId="active-glow"
                  className="dot-glow"
                  transition={{ type: "spring", stiffness: 300, damping: 30 }}
                />
              )}
            </div>
          </div>
        ))}
        {/* Vertical Progress Line */}
        <div className="stepper-line">
          <motion.div 
            className="line-progress"
            style={{
              height: `${((sections.findIndex(s => s.id === activeSection) / (sections.length - 1)) * 100) || 0}%`
            }}
          />
        </div>
      </div>
    </nav>
  );
};

export default SectionStepper;
